import { useCallback, useMemo } from 'react';

import {
  FormatType,
  LineChart,
  type LineData,
  useChartTheme,
  VolumeChart,
  type VolumeChartData,
} from '@revelio/replots';

import {
  type SubsidiaryLineage,
  SubsidiaryLineageDataProvider,
  useGroupLineages,
} from '../../DataProvider/subsidiaries/SubsidiaryLineageDataProvider';
import {
  type PrimaryCompany,
  usePrimaryCompanies,
} from '../../DataProvider/subsidiaries/use-primary-company';
import { useTrendDateRange } from '../../DataProvider/subsidiaries/use-subsidiary-dates';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import type { SubsidiaryTrendKind } from '../../PageConfigProvider/types/item';
import { ChartFallback } from '../ChartFallback';
import { ChartNotSupported } from '../ChartNotSupported';
import { useTableRowsExport } from '../chart-menu/ChartMenuContext';
import {
  buildSubsidiaryTrends,
  type SubsidiaryTrends,
} from './build-subsidiary-trends';
import { setSubsidiaryYearHighlight } from './subsidiary-year-highlight';

const SIDE_CHART_YEAR_TICKS = 4;
const ACQUIRED_LABEL = 'Acquired';
const DIVESTED_LABEL = 'Divested';
const CONCENTRATION_AXIS = { min: 0, max: 1 };
const ACTIVITY_SERIES_LABELS = { new: ACQUIRED_LABEL, removed: DIVESTED_LABEL };

type CompanyTrends = {
  company: PrimaryCompany;
  trends: SubsidiaryTrends;
  color: string;
};

const useCompanyTrends = (lineage: SubsidiaryLineage) => {
  const companies = usePrimaryCompanies();
  const { getPlotColor } = useChartTheme();
  const compared = useMemo(
    () =>
      companies.length > 1
        ? companies
        : [{ rcid: lineage.rcid, name: lineage.rootName }],
    [companies, lineage.rcid, lineage.rootName]
  );
  const others = useMemo(
    () => (compared.length > 1 ? compared : []),
    [compared]
  );
  const { lineages, status } = useGroupLineages(others);
  const range = useTrendDateRange();

  const groups = useMemo((): CompanyTrends[] | undefined => {
    const data = compared.map((company, index) =>
      company.rcid === lineage.rcid ? lineage.data : lineages[index]
    );
    if (data.some((entry) => entry === undefined)) return undefined;
    const start =
      range.start ??
      data
        .map((entry) => entry?.startDate)
        .filter((date): date is string => date !== undefined)
        .sort()[0];
    const now = Math.min(
      Date.now(),
      range.end === null ? Number.POSITIVE_INFINITY : Date.parse(range.end)
    );
    return compared.flatMap((company, index) => {
      const entry = data[index];
      return entry === undefined
        ? []
        : [
            {
              company,
              color: getPlotColor(index),
              trends: buildSubsidiaryTrends({
                data: entry,
                ...(start !== undefined && { start }),
                now,
              }),
            },
          ];
    });
  }, [compared, lineages, lineage.rcid, lineage.data, getPlotColor, range]);

  return { groups, status };
};

const nextYear = (date: string): string =>
  `${Number(date.slice(0, 4)) + 1}-01-01`;

const trendLines = (
  trend: SubsidiaryTrendKind,
  groups: CompanyTrends[]
): LineData[] => {
  const compared = groups.length > 1;
  const singleLabel =
    trend === 'concentration' ? 'Concentration' : 'Subsidiaries';
  return groups.flatMap(({ company, trends, color }) => {
    const own = compared ? { color } : {};
    if (trend === 'activity' && compared) {
      return [
        {
          label: `${company.name} (net)`,
          color,
          values: trends.flows.map((flow) => ({
            date: flow.date,
            value: flow.joined - flow.left,
          })),
        },
      ];
    }
    if (trend === 'activity') {
      return [
        {
          label: ACQUIRED_LABEL,
          values: trends.flows.map((flow) => ({
            date: flow.date,
            value: flow.joined,
          })),
        },
        {
          label: DIVESTED_LABEL,
          values: trends.flows.map((flow) => ({
            date: flow.date,
            value: flow.left,
          })),
        },
      ];
    }
    const values = trends.counts.map((point) => ({
      date: point.date,
      value: trend === 'concentration' ? point.concentration : point.total,
    }));
    return [
      {
        label: compared ? company.name : singleLabel,
        ...own,
        values,
      },
    ];
  });
};

const exportRows = (trend: SubsidiaryTrendKind, lines: LineData[]) => {
  const dateKey = trend === 'activity' ? 'Year' : 'Date';
  const byDate = new Map<string, Record<string, string | number>>();
  for (const line of lines) {
    for (const point of line.values) {
      const key = trend === 'activity' ? point.date.slice(0, 4) : point.date;
      const row = byDate.get(key) ?? { [dateKey]: key };
      row[line.label] = point.value ?? '';
      byDate.set(key, row);
    }
  }
  return [...byDate.values()];
};

const TrendContent = ({
  lineage,
  trend,
}: {
  lineage: SubsidiaryLineage;
  trend: SubsidiaryTrendKind;
}) => {
  const { groups, status } = useCompanyTrends(lineage);

  const drawn = useMemo(
    () => (groups ?? []).filter(({ trends }) => trends.counts.length > 0),
    [groups]
  );
  const lines = useMemo(() => trendLines(trend, drawn), [trend, drawn]);
  const volume = useMemo((): VolumeChartData[] => {
    const flows = drawn[0]?.trends.flows ?? [];
    return [
      {
        id: 'new',
        label: ACQUIRED_LABEL,
        values: flows.map((flow) => ({ date: flow.date, value: flow.joined })),
      },
      {
        id: 'removed',
        label: DIVESTED_LABEL,
        values: flows.map((flow) => ({ date: flow.date, value: flow.left })),
      },
    ];
  }, [drawn]);

  const rows = useMemo(() => exportRows(trend, lines), [trend, lines]);
  useTableRowsExport(rows);

  const flowByDate = useMemo(
    () =>
      new Map(
        (
          drawn.find(({ company }) => company.rcid === lineage.rcid)?.trends
            .flows ?? []
        ).map((flow) => [flow.date, flow])
      ),
    [drawn, lineage.rcid]
  );
  const highlightYear = useCallback(
    (date: Date | null) => {
      const flow =
        date === null
          ? undefined
          : flowByDate.get(date.toISOString().slice(0, 10));
      setSubsidiaryYearHighlight(
        flow === undefined || flow.joined + flow.left === 0
          ? null
          : {
              rcid: lineage.rcid,
              from: flow.date,
              to: nextYear(flow.date),
              ids: flow.joinedIds,
              leftIds: flow.leftIds,
            }
      );
    },
    [flowByDate, lineage.rcid]
  );

  if (groups === undefined) {
    return <ChartFallback status={status} />;
  }

  if (drawn.length === 0) {
    return (
      <ChartNotSupported>
        {groups.length > 1
          ? 'The selected companies have no dated subsidiary history in our data.'
          : `${lineage.rootName} has no dated subsidiary history in our data.`}
      </ChartNotSupported>
    );
  }

  const compared = drawn.length > 1;

  if (trend === 'activity' && !compared) {
    return (
      <VolumeChart
        data={volume}
        format={FormatType.INTEGER}
        dateFormat="year"
        seriesLabels={ACTIVITY_SERIES_LABELS}
        onHoverChange={highlightYear}
        xTickCount={SIDE_CHART_YEAR_TICKS}
      />
    );
  }

  if (trend === 'activity') {
    return (
      <LineChart
        data={lines}
        format={FormatType.INTEGER}
        includeZero
        dateFormat="year"
        curveTension={1}
        xTickCount={SIDE_CHART_YEAR_TICKS}
        onHoverChange={highlightYear}
      />
    );
  }

  return (
    <LineChart
      data={lines}
      format={
        trend === 'concentration' ? FormatType.PERCENTAGE : FormatType.INTEGER
      }
      {...(trend === 'concentration' && { yAxis: CONCENTRATION_AXIS })}
      dateFormat="month"
      curveTension={1}
      xTickCount={SIDE_CHART_YEAR_TICKS}
    />
  );
};

export const SubsidiaryTrend = ({ trend }: { trend: SubsidiaryTrendKind }) => (
  <SubsidiaryLineageDataProvider>
    {({ data, status }) =>
      data === null ? (
        <ChartFallback status={DataProviderQueryStatus.Success}>
          <ChartNotSupported>
            Select a company to see how its group changed over time.
          </ChartNotSupported>
        </ChartFallback>
      ) : (
        <ChartFallback status={status}>
          {data !== undefined && (
            <TrendContent key={data.rcid} lineage={data} trend={trend} />
          )}
        </ChartFallback>
      )
    }
  </SubsidiaryLineageDataProvider>
);
